<template>
    <div>
        <div class="flex align-items-start justify-space-between gap-sm flex-wrap m-t-m" v-show="!showEmptyState || skipEmptyState">
            <div class="flex flex-wrap gap-xs">
                <button type="button" :class="statCardClasses('all')" @click.prevent="toggleStatusFilter('all')">
                    <span class="block upper-label m-b-none m-t-none">All</span>
                    <span class="block font-heavy text-16">{{ loading ? '\u2013' : totalCount }}</span>
                </button>
                <button type="button" :class="statCardClasses('checked_in')" @click.prevent="toggleStatusFilter('checked_in')">
                    <span class="block upper-label m-b-none m-t-none">Checked in</span>
                    <span class="block font-heavy text-16">{{ loading ? '\u2013' : checkedInCount }}</span>
                </button>
                <button type="button" :class="statCardClasses('remaining')" @click.prevent="toggleStatusFilter('remaining')">
                    <span class="block upper-label m-b-none m-t-none">Remaining</span>
                    <span class="block font-heavy text-16">{{ loading ? '\u2013' : remainingCount }}</span>
                </button>
                <button type="button" :class="statCardClasses('absent')" @click.prevent="toggleStatusFilter('absent')">
                    <span class="block upper-label m-b-none m-t-none">Absent</span>
                    <span class="block font-heavy text-16">{{ loading ? '\u2013' : absentCount }}</span>
                </button>
                <button v-if="pendingTrialBookings.length > 0 || statusFilter === 'trial_booked'" type="button" :class="statCardClasses('trial_booked')" @click.prevent="toggleStatusFilter('trial_booked')" title="Booked a trial in this class and not marked yet">
                    <span class="block upper-label m-b-none m-t-none text-yellow-800">Trial booked</span>
                    <span class="block font-heavy text-16">{{ loading ? '\u2013' : pendingTrialBookings.length }}</span>
                </button>
                <button v-if="trialCheckedIn.length > 0 || statusFilter === 'trial'" type="button" :class="statCardClasses('trial')" @click.prevent="toggleStatusFilter('trial')">
                    <span class="block upper-label m-b-none m-t-none text-green-700">Trial</span>
                    <span class="block font-heavy text-16">{{ loading ? '\u2013' : trialCheckedIn.length }}</span>
                </button>
                <button v-if="dropInCheckedIn.length > 0 || statusFilter === 'dropin'" type="button" :class="statCardClasses('dropin')" @click.prevent="toggleStatusFilter('dropin')">
                    <span class="block upper-label m-b-none m-t-none text-blue-700">Drop-in</span>
                    <span class="block font-heavy text-16">{{ loading ? '\u2013' : dropInCheckedIn.length }}</span>
                </button>
            </div>
        </div>
        <div class="form-group m-t-sm" v-show="!showEmptyState || skipEmptyState">
            <div style="position: relative;">
                <input v-model="debouncedSearchTerm" ref="wsearch" type="text" id="wrestler_search" placeholder="Find by name or weight..."
                       class="form-control font-no-ios-zoom"
                       maxlength="25"/>
                <i @click="debouncedSearchTerm = ''" v-if="debouncedSearchTerm && debouncedSearchTerm.length > 0"
                   class="cursor-pointer fa-solid fa-xmark color-gray-400"
                   style="position: absolute; top:0; bottom:0; right: 0; transform: translate(-66%, 29%)"></i>
            </div>
        </div>
        <div>
            <p v-if="error" class="text-danger">
                {{ errorMessage }}
            </p>
        </div>
        <spinner v-if="loading"></spinner>
        <div class="relative" v-if="!loading">
            <check-in-form v-for="wrestler in statusFilteredProfiles"
                           :for-app-embed="forAppEmbed"
                           :key="wrestler.id"
                           :wrestler-profile="wrestler"
                           :check-in="checkInFor(wrestler)"
                           :event-id="event.id"
                           :editable="canEdit"
                           :questions="checkInQuestions"
                           :endpoint="endpoint"
                           :access-key="accessKey"
                           :cloudfront-base-url="cloudfrontBaseUrl"
                           :show-roster-feedback="!!wrestler.outsideFilter"
                           :event-roster-ids="eventRosterIds"
                           :trial-booking-label="trialBookingLabel(wrestler.id)"
            ></check-in-form>
            <p v-if="statusFilter !== 'all' && !specialFilterCheckIns && statusFilteredProfiles.length === 0" class="text-gray-500 m-t-sm">
                No wrestlers in this group.
            </p>
        </div>
        <div v-if="specialFilterCheckIns && !loading">
            <p class="text-12 text-gray-500 m-t-sm m-b-none">
                {{ specialFilterDescription }}
            </p>
            <check-in-form v-for="checkIn in specialFilterCheckIns"
                           :for-app-embed="forAppEmbed"
                           :key="`special-${checkIn.id}`"
                           :wrestler-profile="profileFromCheckIn(checkIn)"
                           :check-in="checkIn"
                           :event-id="event.id"
                           :editable="canEdit"
                           :questions="checkInQuestions"
                           :endpoint="endpoint"
                           :access-key="accessKey"
                           :cloudfront-base-url="cloudfrontBaseUrl"
                           :show-roster-feedback="true"
                           :event-roster-ids="eventRosterIds"
            ></check-in-form>
            <p v-if="specialFilterCheckIns.length === 0" class="text-gray-500 m-t-sm">
                No check-ins in this group yet.
            </p>
        </div>
        <div v-if="search && searchExtras.length > 0 && !loading" class="m-t-md">
            <p class="upper-label m-b-none">Not in current filter</p>
            <check-in-form v-for="wrestler in searchExtras"
                           :for-app-embed="forAppEmbed"
                           :key="`team-search-${wrestler.id}`"
                           :wrestler-profile="wrestler"
                           :check-in="checkInFor(wrestler)"
                           :event-id="event.id"
                           :editable="canEdit"
                           :questions="checkInQuestions"
                           :endpoint="endpoint"
                           :access-key="accessKey"
                           :cloudfront-base-url="cloudfrontBaseUrl"
                           :show-roster-feedback="true"
                           :event-roster-ids="eventRosterIds"
                           :trial-booking-label="trialBookingLabel(wrestler.id)"
            ></check-in-form>
        </div>
        <div v-if="!forAppEmbed && !loading && (!showEmptyState || skipEmptyState)" class="m-t-md text-center">
            <button class="btn btn-default" @click.prevent="downloadCSV">Download Attendance Report</button>
        </div>
        <div v-if="showEmptyState && !skipEmptyState" class="m-t-sm relative border border-solid border-green-600 rounded-xxs">
          <div class="flex align-items-center justify-center gap-sm bg-green-600 text-green-50 uppercase tracking-wide p-x-m p-y-xs -m-sm rounded-t-xxs">
            <span class="text-heavy">WIQ RECOMMENDATION</span>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-green-50">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
            </svg>
          </div>
          <div class="p-x-lg p-y-md grid grid-cols-2 gap-y-sm gap-x-lg sm-grid-cols-1 md-grid-cols-1 m-t-sm">
            <div>
              <h1 class="m-t-none font-heavy">
              Link Rosters To This Event
              </h1>
              <p>
                Connect the appropriate rosters to unlock powerful event management features:
              </p>
              <ul class="p-l-sm">
                <li>Filter attendance to check in the right wrestlers</li>
                <li>Notify only that roster(s) of schedule changes</li>
                <li>Notify wrestlers and parents of this event with event invites</li>
              </ul>
            </div>
            <div class="flex flex-col gap-xl justify-center w-full p-x-md">
              <div class="flex flex-col gap-sm justify-center align-items-center">
                <a @click.prevent="handleEditClick"
                   :href="editEventPath"
                   class="btn btn-primary btn-block w-full justify-center flex align-items-center gap-x-xs">
                  <i class='fa-solid fa-pencil'></i> Edit Event - Link Rosters
                </a>
                <a href="javascript:void(0)" onclick="Beacon('article', '6328c8ff62b5f93d180188ad', { type: 'modal' })" class="btn btn-default btn-block w-full justify-center flex align-items-center gap-x-xs"><i class='fa-solid fa-play'></i>
                  Watch Tutorial Video</a>
              </div>
              <div class="border-t border-solid border-gray-50 p-t-md text-center">
                <button class="btn btn-default btn-block w-full justify-center flex align-items-center gap-x-xs" @click="loadAllWrestlersManually"><i class="fa-solid fa-forward-fast"></i> Skip - Load All Wrestlers For Attendance</button>
                <p class="text-12 text-gray-500">Are you sure? Linking a roster takes < 20 seconds.</p>
              </div>
            </div>
          </div>
        </div>
    </div>
</template>
<script>
import { downloadMix } from './mixins/download_mix';
import { appEmbedMix } from './mixins/app_embed_mix';
import Spinner from './spinner.vue';
import CheckInForm from './check_in_form.vue';

// Mirrors the kiosk attendees view: present and late both count as checked in.
const CHECKED_IN_STATUSES = ['present', 'late'];
const ABSENT_STATUSES = ['absent', 'excused', 'unexcused', 'injured', 'other'];

export default {
  name: 'attendance-list',
  mixins: [downloadMix, appEmbedMix],
  components: {
    CheckInForm,
    Spinner,
  },
  props: {
    event: {
      type: Object,
      required: true,
    },
    endpoint: {
      type: String,
      required: true,
    },
    accessKey: {
      type: String,
      required: true,
    },
    cloudfrontBaseUrl: {
      type: String,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
    // The roster filter is asynchronous, so we need to skip the empty state if we're waiting for that
    skipEmptyState: {
      type: Boolean,
      default: false,
    },
    // Trials booked into this class via the family-facing picker. Coach-only —
    // the server sends an empty list to everyone else. Whether they've actually
    // arrived is answered by checkIns below, not by this list.
    expectedTrials: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      wrestlers: [],
      checkIns: [],
      error: false,
      errorMessage: '',
      wrestlersUrl: null,
      checkInQuestions: [],
      search: '', // This is what we are searching for
      debouncedSearchTerm: '', // This is the model we debounce that the user types in.
      sort: 'last_name',
      rosters: [],
      hasInitializedWrestlers: false,
      // Stat bar filter: 'all' | 'checked_in' | 'remaining' | 'absent' |
      // 'trial' | 'dropin'. 'all' shows the full roll call, with check-ins
      // from outside the roster filter merged into the active sort order.
      statusFilter: 'all',
      // Team-wide search results (server side, ignores the roster filter)
      teamSearchResults: [],
      teamSearchLoading: false,
      // The app hears attendance_saved once per visit, not once per wrestler
      notifiedAppOfSave: false,
    };
  },
  created() {
    const vm = this;
    vm.wrestlers = [];
    vm.wrestlersUrl = null;

    vm.$notificationManager.on('wrestler-checked-in', vm.updateCheckIn);
    vm.$notificationManager.on('roster-filter-applied', vm.changeRoster);
    vm.$notificationManager.on('sort-filter-applied', vm.changeSort);
    this.getSearchResults = _.debounce(this.getSearchResults, 500);
  },
  destroyed() {
    const vm = this;

    if (vm.getSearchResults && vm.getSearchResults.cancel) {
      vm.getSearchResults.cancel();
    }

    vm.$notificationManager.off('wrestler-checked-in', vm.updateCheckIn);
    vm.$notificationManager.off('roster-filter-applied', vm.changeRoster);
    vm.$notificationManager.off('sort-filter-applied', vm.changeSort);
  },
  computed: {
    wrestlerIdSet() {
      return new Set(_.map(this.wrestlers, 'id'));
    },
    // Check-ins whose wrestler is not in the loaded (roster-filtered) list:
    // other rosters, guests, trial, and drop-in wrestlers
    outsideCheckIns() {
      return _.filter(this.checkIns, (c) => !this.wrestlerIdSet.has(c.wrestler_profile_id));
    },
    // Outside check-ins folded into the roll call as wrestler-shaped profiles.
    // Their rows carry roster feedback markup so it's obvious they came from
    // outside the filter.
    outsideProfiles() {
      return _.map(this.outsideCheckIns, (c) => this.profileFromCheckIn(c));
    },
    // Booked trials the coach hasn't marked yet — the "someone is expected
    // tonight" signal. Anything marked (present, late, or any absent reason)
    // drops out, so the card empties as the coach works the roll call.
    pendingTrialBookings() {
      return _.filter(this.expectedTrials, (t) => {
        const status = this.checkInForWrestlerId(t.wrestler_profile_id)?.status;
        return !status || status === 'unknown';
      });
    },
    // Booked-but-unmarked trials whose wrestler isn't already on screen. They
    // are normally on the event's rosters (class passes sync them there), but
    // a coach narrowing the roster filter shouldn't make an expected trial
    // vanish, so fold them in the same way outside check-ins are folded. No
    // need to exclude outsideProfiles here — those all have a check-in, which
    // is exactly what pendingTrialBookings already filtered out.
    expectedTrialProfiles() {
      return _.map(
        _.filter(this.pendingTrialBookings, (t) => !this.wrestlerIdSet.has(t.wrestler_profile_id)),
        (t) => this.profileFromExpectedTrial(t),
      );
    },
    // The full roll call: every loaded wrestler plus everyone with a check-in
    // on this event, merged into the active sort order. Every count and list
    // derives from this, so the numbers always match what's on screen and
    // reconcile with the kiosk attendees view. _.sortBy is stable, so ties
    // keep the server's ordering.
    rollCallProfiles() {
      const combined = this.wrestlers.concat(this.outsideProfiles, this.expectedTrialProfiles);
      return _.sortBy(combined, (w) => this.sortKeyFor(w));
    },
    // Trial and drop-in cards count event-wide (kiosk parity), whether or not
    // the wrestler is in the roster filter.
    trialCheckedIn() {
      return _.filter(this.checkIns, (c) => CHECKED_IN_STATUSES.includes(c.status) && c.class_pass?.paid_session?.session_type === 'trial');
    },
    dropInCheckedIn() {
      return _.filter(this.checkIns, (c) => CHECKED_IN_STATUSES.includes(c.status) && c.class_pass?.paid_session?.session_type === 'dropin');
    },
    // Check-in rows for the pass-based stat cards (event-wide, not a subset of
    // the loaded list)
    specialFilterCheckIns() {
      if (this.statusFilter === 'trial') {
        return this.trialCheckedIn;
      }
      if (this.statusFilter === 'dropin') {
        return this.dropInCheckedIn;
      }
      return null;
    },
    specialFilterDescription() {
      if (this.statusFilter === 'dropin') {
        return 'Checked in to this event using a drop-in pass.';
      }
      return 'Checked in to this event using a trial pass.';
    },
    totalCount() {
      return this.rollCallProfiles.length;
    },
    // Every check-in's wrestler is in rollCallProfiles, so these can count the
    // raw check-ins directly. checkedInCount matches the kiosk attendees count.
    checkedInCount() {
      return _.filter(this.checkIns, (c) => CHECKED_IN_STATUSES.includes(c.status)).length;
    },
    absentCount() {
      return _.filter(this.checkIns, (c) => ABSENT_STATUSES.includes(c.status)).length;
    },
    // Wrestlers with no attendance recorded yet — the coach's "still to call" list
    remainingCount() {
      return this.totalCount - this.checkedInCount - this.absentCount;
    },
    eventRosterIds() {
      return _.map(this.event.roster_events || [], (re) => (re.roster ? re.roster.id : re.roster_id));
    },
    // Applies the stat bar filter on top of the search filter. Rows keep their
    // stable roll-call order; non-matching rows are simply hidden.
    statusFilteredProfiles() {
      // These filters render their own check-in rows via specialFilterCheckIns
      if (['trial', 'dropin'].includes(this.statusFilter)) {
        return [];
      }

      if (this.statusFilter === 'checked_in') {
        return this.searchedProfiles.filter((w) => CHECKED_IN_STATUSES.includes(this.checkInFor(w)?.status));
      }

      if (this.statusFilter === 'absent') {
        return this.searchedProfiles.filter((w) => ABSENT_STATUSES.includes(this.checkInFor(w)?.status));
      }

      if (this.statusFilter === 'remaining') {
        return this.searchedProfiles.filter((w) => {
          const status = this.checkInFor(w)?.status;
          return !status || status === 'unknown';
        });
      }

      if (this.statusFilter === 'trial_booked') {
        return this.searchedProfiles.filter((w) => this.hasPendingTrialBooking(w.id));
      }

      return this.searchedProfiles;
    },
    // Team-wide search hits not already in the roll call (roster list or folded
    // outside check-ins)
    searchExtras() {
      if (!this.search || this.search === '') {
        return [];
      }

      const rollCallIds = new Set(_.map(this.rollCallProfiles, 'id'));
      return this.teamSearchResults.filter((w) => !rollCallIds.has(w.id));
    },
    searchedProfiles() {
      if (!this.search || this.search === '') {
        return this.rollCallProfiles;
      }

      if (isNaN(this.search)) {
        return this.rollCallProfiles.filter((wp) => wp.full_name.toLowerCase()
          .includes(this.search.toLowerCase()));
      }
      return this.rollCallProfiles.filter((wp) => (wp.weight_class || '').includes(this.search));
    },
    // The "link rosters" pitch takes over the card, hiding the stat cards and
    // search. A booked trial is the one thing worth marking on an event with no
    // rosters, so keep the normal chrome whenever one is still pending.
    showEmptyState() {
      return !this.hasInitializedWrestlers
             && this.rosters.length === 0
             && this.pendingTrialBookings.length === 0
             && !this.loading;
    },
    editEventPath() {
      return this.forAppEmbed
        ? `${window.location.origin}/app_embeds/events/${this.event.id}/edit`
        : `/events/${this.event.id}/edit`;
    },
  },
  watch: {
    // whenever search changes, this function will run
    debouncedSearchTerm(newSearch) {
      this.getSearchResults();
    },
  },
  methods: {
    getSearchResults() {
      this.search = this.debouncedSearchTerm;
      this.searchTeamWide();
    },
    // Server-side search across the whole team so a coach can find (and check in)
    // a wrestler outside the current roster filter without changing the filter.
    searchTeamWide() {
      const vm = this;
      const term = (vm.search || '').trim();

      // Numeric terms are weight searches, which stay local to the loaded list
      if (term.length < 2 || !isNaN(term)) {
        vm.teamSearchResults = [];
        return;
      }

      vm.teamSearchLoading = true;
      const url = vm.$apiService.wrestlerNameSearch(term);
      axios.get(url)
        .then((response) => {
          // Ignore stale responses if the term changed while in flight
          if (term === (vm.search || '').trim()) {
            vm.teamSearchResults = response.data.wrestlers || [];
          }
          vm.teamSearchLoading = false;
        })
        .catch(() => {
          vm.teamSearchLoading = false;
        });
    },
    toggleStatusFilter(filter) {
      this.statusFilter = this.statusFilter === filter ? 'all' : filter;
    },
    // Sort key matching the roster filter's server-side sort, so folded
    // outside-filter profiles merge into the right alphabetical position.
    // Folded profiles only have display_name, so first/last name fall back
    // to splitting it.
    sortKeyFor(wrestler) {
      if (this.sort === 'weight') {
        return parseInt(wrestler.weight_class, 10) || 0;
      }

      if (this.sort === 'dob') {
        return wrestler.dob || wrestler.date_of_birth || '';
      }

      const fullName = wrestler.full_name || '';
      if (this.sort === 'first_name') {
        return (wrestler.first_name || fullName.split(' ')[0] || '').toLowerCase();
      }

      const lastName = wrestler.last_name || fullName.split(' ').slice(-1)[0] || '';
      return lastName.toLowerCase();
    },
    statCardClasses(filter) {
      return {
        'attendance-stat-card': true,
        'attendance-stat-card--active': this.statusFilter === filter,
      };
    },
    // Adapts a check-in's embedded profile to the wrestler shape check-in-form
    // expects, for check-ins whose wrestler is not in the loaded list. The
    // outsideFilter flag turns on the roster feedback markup for their rows.
    profileFromCheckIn(checkIn) {
      const profile = checkIn.profile || {};
      return {
        id: checkIn.wrestler_profile_id,
        full_name: profile.display_name,
        weight_class: profile.weight_class != null ? String(profile.weight_class) : null,
        academic_class: profile.academic_class,
        date_of_birth: profile.date_of_birth,
        rosters: profile.rosters || [],
        outsideFilter: true,
      };
    },
    // Same adaptation for an expected trial, whose payload wraps the wrestler
    // in the identical `profile` shape the check-in serializer uses.
    profileFromExpectedTrial(expectedTrial) {
      const profile = expectedTrial.profile || {};
      return {
        id: expectedTrial.wrestler_profile_id,
        full_name: profile.display_name,
        weight_class: profile.weight_class != null ? String(profile.weight_class) : null,
        academic_class: profile.academic_class,
        date_of_birth: profile.date_of_birth,
        rosters: profile.rosters || [],
        outsideFilter: true,
      };
    },
    checkInForWrestlerId(wrestlerProfileId) {
      return _.find(this.checkIns, (check) => check.wrestler_profile_id === wrestlerProfileId);
    },
    hasPendingTrialBooking(wrestlerProfileId) {
      return _.some(this.pendingTrialBookings, (t) => t.wrestler_profile_id === wrestlerProfileId);
    },
    // Session name for the chip on the row, e.g. "Trial booked — Free Trial Week"
    trialBookingLabel(wrestlerProfileId) {
      const booking = _.find(this.pendingTrialBookings, (t) => t.wrestler_profile_id === wrestlerProfileId);
      if (!booking) {
        return null;
      }
      return booking.session_name || 'Trial';
    },
    changeRoster(rosters, strategy) {
      const vm = this;
      vm.rosters = rosters;
      vm.wrestlers = [];

      if (rosters.length > 0) {
        vm.hasInitializedWrestlers = true;
      }

      if (rosters.length === 0 && !vm.hasInitializedWrestlers) {
        vm.wrestlersUrl = null;
        vm.loading = false;
        return;
      }

      vm.checkForFilterChange();
    },
    changeSort(sort) {
      const vm = this;
      vm.sort = sort;

      // Don't trigger a load if we haven't initialized wrestlers yet
      if (!vm.hasInitializedWrestlers) {
        return;
      }

      vm.checkForFilterChange();
    },
    checkForFilterChange() {
      const rosterIds = _.map(this.rosters, 'id');
      // Only include guests if we have roster filters
      const includeGuests = rosterIds.length > 0;
      const newUrl = this.withEventId(this.$apiService.filteredWrestlers(includeGuests, rosterIds, this.sort, 100, 'union', true));

      if (newUrl !== this.wrestlersUrl) {
        this.wrestlersUrl = newUrl;
        this.loadAllWrestlers();
      }
    },
    loadAllWrestlers: _.throttle(function () {
      const vm = this;
      if (vm.loading || !vm.wrestlersUrl) {
        return;
      }

      // Store the current URL to compare after the request
      const requestUrl = vm.wrestlersUrl;

      vm.loading = true;
      vm.error = false;

      vm.$apiService.loadAllPages(requestUrl, 'wrestlers')
        .then((objects) => {
          // Only update if the URL hasn't changed during the request
          if (requestUrl === vm.wrestlersUrl) {
            vm.wrestlers = objects;
            vm.loadAllCheckIns();
          } else {
            // If URL changed, we're already loading new data
            vm.loading = false;
          }
        })
        .catch((error) => {
          vm.loading = false;
          vm.errorMessage = `Error retrieving wrestlers ${error.toString()}`;
          vm.error = true;
        });
    }, 500),
    loadAllCheckIns() {
      const vm = this;
      const url = vm.$apiService.checkInsUrl(vm.event.id);

      vm.$apiService.loadAllPages(url, 'check_ins')
        .then((objects) => {
          vm.checkIns = objects;
          vm.loadAllRegistrationQuestions();
        })
        .catch((error) => {
          vm.loading = false;
          vm.errorMessage = `Error retrieving attendance check-ins ${error.toString()}`;
          vm.error = true;
        });
    },
    loadAllRegistrationQuestions() {
      const vm = this;
      const url = vm.$apiService.registrationQuestions('CheckIn');

      vm.$apiService.loadAllPages(url, 'registration_questions')
        .then((objects) => {
          vm.checkInQuestions = objects;
          vm.loading = false;
        })
        .catch((error) => {
          vm.loading = false;
          vm.errorMessage = `Error retrieving custom check in questions ${error.toString()}`;
          vm.error = true;
        });
    },
    checkInFor(wrestler) {
      const vm = this;
      const wrestlerId = wrestler.id;

      return _.find(vm.checkIns, (check) => check.wrestler_profile_id === wrestlerId);
    },
    buildLocalCheckIn(id, status, wrestler) {
      return {
        id,
        status,
        wrestler_profile_id: wrestler.id,
        profile: {
          id: wrestler.id,
          display_name: `${wrestler.first_name} ${wrestler.last_name}`,
          avatar: wrestler.avatar,
          weight_class: wrestler.weight_class,
          academic_class: wrestler.academic_class,
          date_of_birth: wrestler.dob,
          rosters: wrestler.rosters || [],
        },
        event_id: this.event.id,
        questions: this.checkInQuestions,
        answers: [],
        notes: '',
        class_pass: {},
      };
    },
    // This is a pretty terrible perversion of Vue I committed a long time ago.
    // This uses the event bus to keep this parent data in sync with the
    // data being manipulated in the check in form. It works this way
    // because I did not understand javascript objects are passed by reference.
    // If I were to build this from the ground, I would build the local check ins first
    // however, this does keep the data source of truth clean (here), the child components
    // just manipulate the data and essentially commit a 'mutation' against the data in Vuex terms

    // One more udpate: I do not understand how this works. This emit event seems to keep the list in sync
    // But without updating the child component.. oohhh I bet it's because we did not use $set
    // So what's the real answer I would want here.
    updateCheckIn(checkInId, status, wrestlerProfile, notes, classPass) {
      const vm = this;
      let checkIn = vm.checkInFor(wrestlerProfile);

      if (!checkIn) {
        checkIn = vm.buildLocalCheckIn(checkInId, status, wrestlerProfile);
      }

      // set the new status regardless of new or updated
      checkIn.status = status;
      checkIn.notes = notes;
      checkIn.class_pass = classPass;

      // Insert the check in to our source of truth after it's been manipulated here.
      const index = _.findIndex(vm.checkIns, { id: checkInId });
      if (index !== -1) {
        vm.checkIns.splice(index, 1, checkIn);
      } else {
        vm.checkIns.push(checkIn);
      }

      vm.notifyAppOfSave();

      // Clear the search field after taking action so the coach doesn't have to
      // manually clear their filter before checking in the next wrestler. The
      // watcher resets `search` and the team-wide results.
      vm.debouncedSearchTerm = '';
    },
    // Taking attendance counts toward the app's App Store review prompt.
    // Attendance saves in place and never pops, so tell the app directly.
    notifyAppOfSave() {
      if (this.notifiedAppOfSave) {
        return;
      }
      this.notifiedAppOfSave = true;
      this.notifyApp({ name: 'attendance_saved', data: { event_id: this.event.id } });
    },
    // Builds the report notes without mutating the check-in — these objects also
    // render in the list, so appending pass info in place would corrupt the UI
    // (and stack up on repeat downloads).
    notesForCheckIn(checkIn) {
      if (!checkIn) {
        return undefined;
      }

      let notes = checkIn.notes || '';
      const sessionType = checkIn.class_pass?.paid_session?.session_type;
      if (sessionType === 'trial') {
        notes = `${notes} Trial Pass Used: ${checkIn.class_pass.paid_session.name}`.trim();
      }
      if (sessionType === 'dropin') {
        notes = `${notes} Drop In Pass Used: ${checkIn.class_pass.paid_session.name}`.trim();
      }

      return notes;
    },
    downloadCSV() {
      const vm = this;

      const rows = [];
      rows.push([
        'Last name',
        'First name',
        'Status',
        'Notes',
      ]);

      // The roll call already includes every loaded wrestler plus everyone with
      // a check-in (trial, drop-in, outside the roster filter), in the same
      // order as on screen. Folded profiles only have full_name, so split it.
      vm.rollCallProfiles.forEach((wrestler) => {
        const checkIn = vm.checkInFor(wrestler);
        const fullName = wrestler.full_name || '';
        const lastName = wrestler.last_name || fullName.split(' ').slice(-1)[0] || '';
        const firstName = wrestler.first_name || fullName.split(' ').slice(0, -1).join(' ');

        rows.push([
          lastName,
          firstName,
          _.capitalize(checkIn?.status),
          vm.notesForCheckIn(checkIn),
        ]);
      });

      const fileName = `Attendance Report - ${vm.event.name} ${moment(vm.event.start_at)
        .format('M/D/YYYY')}.csv`;
      this.downloadCSVFile(fileName, rows);
    },
    // Scopes each wrestler's weekly-limit chip to this event (a camp doesn't
    // count against a membership's weekly cap).
    withEventId(url) {
      return `${url}&event_id=${this.event.id}`;
    },
    loadAllWrestlersManually() {
      this.hasInitializedWrestlers = true;
      this.wrestlersUrl = this.withEventId(this.$apiService.filteredWrestlers(false, [], this.sort, 100, 'union', true));
      this.loadAllWrestlers();
    },
    handleEditClick(e) {
      if (this.forAppEmbed) {
        this.askAppToPush(this.editEventPath, 'Edit Event');
      } else {
        // Regular web navigation
        window.location.href = this.editEventPath;
      }
    },
  },
};
</script>
